import { isNil, omitBy } from 'lodash';
import { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import useDataLayerHashedValues from './useDataLayerHashedValues';
import { useApplicationQuery, useClientQuery } from 'hooks/webapi';
import useClientPhoneQuery from 'hooks/webapi/client/useClientPhoneQuery';
import useClientEmailQuery from 'hooks/webapi/client/useClientEmailQuery';

const tagManagerArgs = {
  gtmId: import.meta.env.VITE_GTM || '',
};

export default function useDataLayer() {
  const { data: clientData } = useClientQuery();
  const { data: applicationData } = useApplicationQuery();
  const { data: mobilePhoneData } = useClientPhoneQuery();
  const { data: emailData } = useClientEmailQuery();
  const prevLocation = useRef<string>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const {
    getHashedEmail,
    getHashedFirstName,
    getHashedLastName,
    getHashedTelephoneNumber,
  } = useDataLayerHashedValues();

  const logPageChange = async () => {
    try {
      const dataLayer: Record<string, any> = {
        event: 'pageChange',
        page: {
          url: window.location.pathname,
          referrer: prevLocation.current ?? document.referrer,
        },
      };

      if (clientData) {
        dataLayer.client = omitBy(
          {
            id: clientData.id,
            dateOfBirth: clientData.dateOfBirth,
            number: clientData.number,
            status: clientData.status,
            age: clientData.age,
            identifiedBy: clientData.identifiedBy,
            registeredBy: clientData.registeredBy,
            htn: mobilePhoneData
              ? await getHashedTelephoneNumber(mobilePhoneData.mobilePhone)
              : undefined,
            hma: emailData ? await getHashedEmail(emailData.email) : undefined,
            address: omitBy(
              {
                hfn: await getHashedFirstName(clientData.firstName),
                hln: await getHashedLastName(clientData.lastName),
                city: clientData.declaredAddress?.location1,
                'postal-code': clientData.declaredAddress?.postalCode,
              },
              isNil,
            ),
          },
          isNil,
        );
      }

      if (applicationData && applicationData.status === 'OPEN') {
        dataLayer.application = omitBy(
          {
            id: applicationData.id,
            amount: applicationData.amount,
            term: applicationData.term,
          },
          isNil,
        );
      }

      TagManager.dataLayer({
        dataLayer,
      });

      prevLocation.current = window.location.pathname;
    } catch (e) {
      console.error('Error logging page change do GTM dataLayer', e);
    }
  };

  const decouncedLogPageChange = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(logPageChange, 2000);
  };

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);

    // Track basic JavaScript errors
    window.addEventListener('error', function (e) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'error',
          message: e.message,
          stacktrace: e.error?.stack,
          path: window.location.pathname,
        },
      });
    });

    if (import.meta.env.VITE_GTM) {
      // Log the initial page load
      logPageChange();
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.VITE_GTM) {
      decouncedLogPageChange();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [clientData, applicationData]);
}
